
/**
 * Helper function to convert an ArrayBuffer to a URL-safe base64 string
 * @param {Uint8Array} arrayBuffer
 * @returns {string} The base64 URL encoded string
 */
export function base64URLEncode(arrayBuffer: Uint8Array): string {
  return btoa(String.fromCharCode(...arrayBuffer))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Generates a random string.
 *
 * This function generates a random string using the window.crypto.getRandomValues method,
 * which fills a Uint8Array with cryptographically strong random values.
 *
 * If the generateAsciiOnly parameter is true, the function generates a string of random ASCII characters.
 * It does this by converting each random value to a number between 32 and 126, which corresponds to the range of printable ASCII characters,
 * and then converting this number to an ASCII character using String.fromCharCode.
 *
 * If the generateAsciiOnly parameter is false or not provided, the function generates a random base64 string.
 * It does this by encoding the Uint8Array of random values as a base64 string.
 *
 * @param {boolean} generateAsciiOnly - Optional
 * @returns {string} The generated random string.
 */
export function generateRandomString(
  generateAsciiOnly: boolean = false,
  length: number = 32,
): string {
  if (generateAsciiOnly) {
    let result = '';
    const chars =
      '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (let i = length; i > 0; --i) {
      result += chars[Math.round(Math.random() * (chars.length - 1))];
    }
    return result;
  } else {
    // Adjust the length for base64 encoding so it ends up being 128 characters
    // When you encode a string to base64, the length of the encoded string is always 4/3 of the length of the original string.
    length = Math.floor(length * 0.75);
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return base64URLEncode(array);
  }
}

export async function launchUri(url: string) {
  const windowProxy = window.open(url, '_self');
  if (windowProxy) {
    return Promise.resolve(windowProxy);
  } else {
    return Promise.reject(new Error('Failed to open the URL'));
  }
}

/**
  /* encodeURIComponent is not URL safe, use urlSafeEncode instead. Cognito 
    single-encodes/decodes url on first sign in and double-encodes/decodes url
    when user already signed in. Using encodeURIComponent, Base32, Base64 add 
    characters % or = which on further encoding becomes unsafe. '=' create issue 
    for parsing query params. 
    Refer: https://github.com/aws-amplify/amplify-js/issues/5218 
   * @param str 
   * @returns 
   */
export function urlSafeEncode(str: string) {
  return str
    .split('')
    .map(char => char.charCodeAt(0).toString(16).padStart(2, '0'))
    .join('');
}

export async function sha256(text: string) {
  const encoder = new TextEncoder(); // encode to Uint8Array
  const data = encoder.encode(text); // encode the text into bytes [0, 255]
  const hashBuffer = await crypto.subtle.digest('SHA-256', data); // hash the data
  const hashArray = new Uint8Array(hashBuffer); // convert buffer to byte array
  return base64URLEncode(hashArray); // convert bytes to base64 url-safe string
}

/**
 * Generates a challenge from a given text
 * see PKCE spec: https://tools.ietf.org/html/rfc7636
 * @param {string} text
 * @returns {string} The generated challenge
 */
export async function generateChallenge(code: string) {
  const base64Code = await sha256(code);
  return base64Code;
}
